import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import logo from "./wyllie-home.png";
import "./App.scss";
import { Routes, Route, Navigate } from "react-router";
import LaunchPad from "./Pages/LaunchPad";
import { Photo } from "./Contract/Photos";
import { useAsyncEffect } from "./services/hooks";
import { PhotosApi } from "./services/photoService";
import Home from "./Pages/Home/Home";
import { getWeather } from "./services/weatherService";
import { WeatherApiResponse } from "./Contract/Weather";
import Admin from "./Pages/Admin/Admin";
import { GoogleCalTokenApi } from "./services/googleTokenApi";
import GoogleOAuthCallback from "./Components/GoogleOAuthCallback";
import SpotifyOAuthCallback from "./Components/SpotifyOAuthCallback";
import { photoCacheService } from "./services/photoCacheService";
import RequireAuth from "./Components/RequireAuth";
import Login from "./Pages/Login";
import { AUTH_SESSION_CHANGED_EVENT, isLoggedIn } from "./services/authSession";
import { Phase, useDayPhase } from "./services/dayPhase";
import { AdminAuthService } from "./services/adminAuthService";

// While the house render is being worked on, keep the display on it instead
// of rotating through photos. Set to false to bring the photos back.
const HOUSE_ONLY = true;

export type GlobalContext = {
  photos: Photo[];
  weather?: WeatherApiResponse;
  calendarToken?: string;
  setCalendarToken: Dispatch<SetStateAction<string | undefined>>;
  currentPhotoIndex: number;
  currentPhotoStyle: React.CSSProperties;
  /** True while the rotation is on the 3D house render instead of a photo. */
  showingHouse: boolean;
  /** Time of day; drives the colour theme and the house video. */
  phase: Phase;
  setPhotos: Dispatch<SetStateAction<Photo[]>>;
};
export const Context = createContext<GlobalContext>({
  photos: [],
  calendarToken: undefined,
  currentPhotoStyle: {},
  currentPhotoIndex: 0,
  showingHouse: true,
  phase: "day",
  setPhotos: () => {},
  setCalendarToken: () => {},
});

function App() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [cachedPhotoUrls, setCachedPhotoUrls] = useState<Map<string, string>>(
    new Map(),
  );
  const [weather, setWeather] = useState<WeatherApiResponse | undefined>(
    undefined,
  );

  const [calendarToken, setCalendarToken] = useState<string | undefined>(
    undefined,
  );
  // Slide 0 is the 3D house render; slide n shows photos[n - 1].
  const [slide, setSlide] = useState(0);
  const currentPhotoIndex = Math.max(0, slide - 1);
  const showingHouse = HOUSE_ONLY || slide === 0 || photos.length === 0;
  const [authenticated, setAuthenticated] = useState(isLoggedIn());

  useEffect(() => {
    const refreshAuth = () => setAuthenticated(isLoggedIn());
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, refreshAuth);
    window.addEventListener("storage", refreshAuth);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, refreshAuth);
      window.removeEventListener("storage", refreshAuth);
    };
  }, []);

  // Keep the session fresh while the hub is running.
  useEffect(() => {
    if (!authenticated) return;
    const refresh = () =>
      new AdminAuthService()
        .refreshSession()
        .catch((e) => console.error("Failed to refresh session:", e));
    refresh();
    const interval = setInterval(refresh, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [authenticated]);

  useAsyncEffect(async () => {
    if (!authenticated) {
      setPhotos([]);
      setCalendarToken(undefined);
      return;
    }
    const photos = await new PhotosApi().getPhotos();
    setPhotos(photos);
    try {
      setWeather(await getWeather());
    } catch (e) {
      console.error("Failed to load weather:", e);
    }
    const token = await new GoogleCalTokenApi().getGoogleCalToken();
    setCalendarToken(token?.accessToken);
  }, [authenticated]);

  // Auto-refresh photos every 10 minutes to detect new uploads
  useEffect(() => {
    if (!authenticated) return;
    const refreshPhotos = async () => {
      try {
        const newPhotos = await new PhotosApi().getPhotos();
        // Always refresh: presigned S3 URLs expire and need periodic renewal.
        setPhotos(newPhotos);
      } catch (e) {
        console.error("Failed to refresh photos:", e);
      }
    };

    void refreshPhotos();

    // Check for new photos every 10 minutes
    const interval = setInterval(refreshPhotos, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [authenticated]);

  // Auto-refresh weather every 5 hours to detect new uploads
  useEffect(() => {
    if (!authenticated) return;
    const refreshWeather = async () => {
      try {
        const newWeather = await getWeather();
        setWeather(newWeather);
      } catch (e) {
        console.error("Failed to refresh weather:", e);
      }
    };
    // Check for new weather every 5 hours
    const interval = setInterval(refreshWeather, 5 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [authenticated, weather]);

  useEffect(() => {
    if (HOUSE_ONLY) return;
    const timer = setInterval(() => {
      setSlide((prev) => (prev + 1) % (photos.length + 1));
    }, 60000);
    return () => clearInterval(timer);
  }, [photos]);

  useEffect(() => {
    if (slide > photos.length) {
      setSlide(0);
    }
  }, [slide, photos.length]);

  // Update cache when photos change (e.g., when user adds/deletes photos)
  useEffect(() => {
    const updateCache = async () => {
      const urlMap = new Map<string, string>();
      for (const photo of photos) {
        const cachedUrl = await photoCacheService.getCachedPhotoUrl(photo);
        urlMap.set(photo.id, cachedUrl);
      }
      setCachedPhotoUrls(urlMap);
    };

    if (photos.length > 0) {
      updateCache();
    }

    // Cleanup: Revoke blob URLs when component unmounts
    return () => {
      cachedPhotoUrls.forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [photos]);

  const currentPhoto = showingHouse ? undefined : photos[currentPhotoIndex];

  const astro = weather?.forecast.forecastday[0]?.astro;
  const phase = useDayPhase(astro?.sunrise, astro?.sunset);
  useEffect(() => {
    document.documentElement.dataset.theme = phase;
  }, [phase]);
  const currentPhotoUrl = currentPhoto
    ? cachedPhotoUrls.get(currentPhoto.id) || currentPhoto.url
    : undefined;

  return (
    <Context.Provider
      value={{
        photos,
        setCalendarToken,
        setPhotos,
        weather,
        currentPhotoIndex,
        showingHouse,
        phase,
        calendarToken,
        currentPhotoStyle: {
          backgroundImage: currentPhotoUrl
            ? `url(${currentPhotoUrl})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transition: "background-image 1s ease-in-out",
        },
      }}
    >
      <div className="App">
        <Routes>
          <Route
            path="/"
            element={
              isLoggedIn() ? <LaunchPad /> : <Navigate to="/login" replace />
            }
          />
          <Route path="/launchpad" element={<LaunchPad />} />
          <Route
            path="/home-hub"
            element={
              <RequireAuth>
                <Home />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <Admin />
              </RequireAuth>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/oauth/callback" element={<GoogleOAuthCallback />} />
          <Route
            path="/oauth/spotify-callback"
            element={<SpotifyOAuthCallback />}
          />
        </Routes>
      </div>
    </Context.Provider>
  );
}

export default App;
